
class BYOSBuilder extends HTMLElement {
    constructor() {
        super();
        this.selectors = {
            productCardAction: '[data-build-set-product="product-selection"]',
            productCard: '[data-build-set="product-card"]',
            removeCard: '[data-build-set-product="remove-product"]',
            selectedProducts: '[data-build-set-product="selected-products"]',
            itemCount: '[data-build-set-product="item-count"]',
            setPrice: '[data-build-set-product="set-price"]',
            setSalePrice: '[data-build-set-product="set-sale-price"]',
            addToCart: '[data-build-set-product="custom-add-to-cart"]',
            giftProduct: '[data-build-set-product="gift-product"]',
            sidebarToggle: '[data-build-set-product="sidebar-toggle"]',
            giftText : '[data-build-set-product="gift-text"]'
        };
        this.setProductTitle = this.getAttribute("data-build-set-title") || '';
        this.setProductImage = this.getAttribute("data-build-set-image") || '';
        this.minSelectionsForCart = parseInt(this.querySelector(this.selectors.selectedProducts).getAttribute("min-selection"));

        this.titleMaxLength = 29;
        this.selectedProducts = [];
        this.existingCartProducts = [];
        this.isUpdatingCart = false;
        this.giftProductStatus =
            this.querySelector(this.selectors.giftProduct)?.getAttribute(
                "data-gift-product"
            ) || false;

        this.attachEvents();
    }

    get elements() {
        return {
            allProducts: () => this.querySelectorAll('[data-build-set-product="product-card"]'),
            productCountElem: () => this.querySelector('[data-product-count]'),
            filterList: () => this.querySelector('[data-build-set="filter-list"]'),
            productCardActions: () => this.querySelectorAll(this.selectors.productCardAction),
            selectedProductsDisplay: () => this.querySelector('[data-build-set-product="selected-products"]'),
            itemCountElement: () => this.querySelector(this.selectors.itemCount),
            setPriceElement: () => this.querySelector(this.selectors.setPrice),
            addToCartButton: () => this.querySelector(this.selectors.addToCart),
        }
    }

    attachEvents() {
        const { elements: { filterList, productCardActions, addToCartButton } } = this;
        filterList()?.addEventListener('click', this.handleFilter.bind(this));
        productCardActions().forEach((card) => {
            card.addEventListener('click', (e) => this.handleProductSelection(e));
        })
        addToCartButton().addEventListener('click', (e) => this.handleAddToCart(e));
        document.addEventListener('byos-cart:updated', ()=> {
            this.checkInitialCartState();
        })
        this.querySelector(this.selectors.sidebarToggle)?.addEventListener(
            "click",
            this.handleSidebarToggle.bind(this)
        );
    }

    handleFilter(event) { /* Handle Filter */
        event.preventDefault();
        const { elements: { filterList, allProducts, productCountElem } } = this;
        let visibleCount = 0;

        const button = event.target.closest('[data-build-set="filter"]');
        if (!button) return;

        filterList().querySelectorAll('.build-set__filter-option').forEach(list => list.classList.remove('is-active'));
        button.classList.add('is-active');

        const filterValue = button.dataset.filterBy;

        allProducts().forEach(card => {
            const matchesFilter = filterValue === 'all' || card.dataset.productCategory === filterValue;
            card.hidden = !matchesFilter;
            if (matchesFilter) visibleCount++;
        });

        if (productCountElem()) productCountElem().textContent = visibleCount;
    }

    async handleProductSelection(event) { /* Select Products from Build */
        const button = event.currentTarget;
        const card = button.closest("product-card");
        if (!card) return;

        const variantId = card.dataset.variantId;
        const isSelected = card.dataset.buildSetSelected === "true";

        if (isSelected) {
            this.removeProduct(card, variantId);
            this.updateUIState();
            return;
        }

        if (this.selectedProducts.length >= this.minSelectionsForCart) {
            document.dispatchEvent( /* Dispatch toast for max selection reached */
                new CustomEvent("toast:show", {
                    detail: {
                        message: `You can select a maximum of ${this.minSelectionsForCart} items.`,
                        tone: "error",
                        duration: 4000,
                    },
                })
            );
            return;
        }

        const productData = { /* Gather product info */
            id: variantId,
            title: card.dataset.productTitle,
            variantTitle: card.dataset.variantTitle,
            price: parseInt(card.dataset.productPrice, 10),
            image: card.querySelector("img")?.src || "",
            path: window.location.pathname
        };

        this.addProduct(card, productData);
        this.updateUIState();
    }

    addProduct(card, variantData) {
        if (!card || !variantData) return;

        const product = {
            variantId: variantData.id,
            title: variantData.title || "",
            variantTitle: variantData.variantTitle || "",
            price: Number(variantData.price) || 0,
            image: variantData?.image || "",
            path: window.location.pathname
        };

        this.selectedProducts.push(product);
        card.dataset.buildSetSelected = "true";
    }

    async checkInitialCartState(type) {
        this.selectedProducts = [];
        this.existingCartProducts = [];

        try {
            const response = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await response.json();

            let hasOutOfStockItems = false;

            cart.items.forEach((item) => {
                const { properties, quantity, variant_id, product_title, price, image, variant_title, key } = item;
                if (!properties || quantity !== 1) return;
                if (properties._is_byos === "true" && properties._byos_url == window.location.pathname) {
                    const productCard = this.querySelector(`product-card[data-variant-id="${variant_id}"]`);
                    const input = productCard.querySelector(this.selectors.productCardAction);
                    if (!productCard) return;

                    const isOutOfStock = productCard
                        .querySelector('[data-build-set-product="product-selection"]')
                        ?.hasAttribute("disabled");

                    const existingItem = {
                        variantId: variant_id,
                        title: product_title,
                        price,
                        image,
                        variantTitle: variant_title,
                        key,
                        ...(isOutOfStock && { outOfStock: true }),
                    };

                    productCard.dataset.buildSetSelected = isOutOfStock ? "oos" : "true";
                    input.checked = isOutOfStock ? "oos" : "true";
                    if (isOutOfStock) hasOutOfStockItems = true;

                    this.selectedProducts.push(existingItem);
                    this.existingCartProducts.push(existingItem);
                    this.isUpdatingCart = true;
                }

                else if (properties._is_byos_gift === "true") {
                    const existingGiftItem = {
                        variantId: variant_id,
                        gift: "true",
                        price,
                        key,
                        path: properties._byos_url
                    };

                    this.selectedProducts.push(existingGiftItem);
                    this.existingCartProducts.push(existingGiftItem);
                }
            });

            if (this.selectedProducts.length === 0) {
                this.querySelectorAll('[data-build-set-product="product-card"]').forEach((card) => {
                    card.dataset.buildSetSelected = card.dataset.buildSetSelected === "true" ? "false" : card.dataset.buildSetSelected;
                    const input = card.querySelector(this.selectors.productCardAction);
                    if(input) input.checked = false;
                });
            }
            if (hasOutOfStockItems) {
                this.querySelector(".build-set__error-banner")?.classList.remove("hidden");
            }

        } catch (error) {
            console.error("Error fetching cart:", error);
        } finally {
            if (type !== "reload") {
                this.updateUIState();
            }
        }
    }

    removeProduct(card, variantId) {
        if (!card || !variantId) return;

        this.selectedProducts = this.selectedProducts.filter(
            product => product.variantId != variantId
        );
        const input = card.querySelector(this.selectors.productCardAction);
        card.dataset.buildSetSelected = card.dataset.buildSetSelected === "oos" ? "oos" : "false";
        if(input) input.checked = false;

    }

    updateSelectedProductsDisplay() {
        const { elements: { selectedProductsDisplay } } = this;
        const container = selectedProductsDisplay();
        if (!container) return;

        const cards = container.querySelectorAll('.build-set__selected-product-card');
        const nonGiftProducts = this.selectedProducts.filter(p => !p.gift);

        cards.forEach((card, index) => {
            const product = nonGiftProducts[index];
            const imageEl = card.querySelector('.build-set__selected-product-image');
            const titleEl = card.querySelector('.build-set__selected-product-name');
            const variantEl = card.querySelector('.build-set__selected-variant-title');
            const promoEl = card.querySelector('.build-set__selected-product-promo');

            if (product) {
                card.dataset.variantId = product.variantId;
                const productCard = this.querySelector( '[data-build-set-product="product-card"][data-variant-id="'+product.variantId+'"]');
                const getVarTitleVal = productCard.getAttribute('data-variant-title');
                imageEl.innerHTML = `<img src="${product.image}" alt="${product.title}">`;
                const title = product.title;
                const ellipseTitle =  title.length > this.titleMaxLength ? title.slice(0, this.titleMaxLength) + "..." : title;
                titleEl.textContent = ellipseTitle;

                let variantText = product.variantTitle || "";
                if(getVarTitleVal){ variantText = getVarTitleVal; }
                if (variantText.includes("oz.")) {
                    const parts = variantText.split("|");
                    variantText = parts[parts.length - 1].trim();
                }

                if (product.outOfStock) {
                    variantText = "Out of Stock";
                    variantEl.classList.add("text-error");
                } else {
                    variantEl.classList.remove("text-error");
                }

                variantEl.textContent = variantText;
                card.classList.add("is-selected");
                if (promoEl) promoEl.style.display = "none"; // hide promo if selected
                return;
            }

            card.dataset.variantId = "";
            imageEl.innerHTML = "";
            titleEl.textContent = "Add Item";
            variantEl.textContent = "";
            card.classList.remove("is-selected");

            const slotIndex = index + 1;
            if (
                promoEl &&
                (slotIndex === this.minSelectionsForCart ||
                    slotIndex === this.minSelectionsForCart)
            ) {
                promoEl.style.display = "";
            }
        });
    }

      formatPrice(price) {
        return (price / 100).toLocaleString('en-US', {
            style: "currency",
            currency: window.Shopify.currency.active,
            currencyDisplay: 'symbol'
        });
    }

    updatePriceDisplay() {
        const priceElement = this.querySelector(this.selectors.setPrice);
        const nonGiftProducts = this.selectedProducts.filter((product) => !product.gift);
        const subtotal = nonGiftProducts.reduce((sum, item) => sum + item.price, 0);
        let finalPrice = subtotal;
        const formattedPrice = this.formatPrice(finalPrice);
        priceElement.textContent = formattedPrice;
    }

    updateProgressBar() {
        const progressBar = this.querySelector(".build-set-status-bar");
        const steps = this.querySelectorAll(".build-set-step");
        if (!progressBar || !steps.length) return;

        const nonGiftProducts = this.selectedProducts.filter(p => !p.gift);
        let selectedCount = nonGiftProducts.length;
        const totalSteps = this.minSelectionsForCart + (this.giftProductStatus ? 1 : 0);
        let percentage = 0;
        if (selectedCount >= this.minSelectionsForCart) {
            percentage = 100;
            selectedCount = selectedCount + 1;
        } else if (selectedCount > 1) {
            percentage = ((selectedCount - 1) / (totalSteps - 1)) * 100;
        }
        progressBar.style.width = `${Math.min(percentage, 100)}%`;
        steps.forEach((step, i) => {
            step.classList.toggle("build-set-step--active", i < selectedCount);
        });
    }

    updateAddToCartButton() {
        const { elements: { addToCartButton } } = this;
        const oosProducts = this.selectedProducts.filter(
            (product) => product.outOfStock
        );
        const isEnabled = this.selectedProducts.length >= this.minSelectionsForCart;
        addToCartButton().disabled = !isEnabled;
        const buttonContent = addToCartButton().querySelector("button-content");
        if (this.isUpdatingCart) {
            buttonContent.textContent = "Update Set";
        }
        if (isEnabled) {
            const btnText = addToCartButton().querySelector("button-content").getAttribute('button-text');
            addToCartButton().querySelector("button-content").textContent = btnText;
        } else {
            let btnText = `Add ${this.minSelectionsForCart} Items to set`;
            if (window.innerWidth < 1000) {
                btnText = `Add ${this.minSelectionsForCart} Items`;
            }
            addToCartButton().querySelector(
                "button-content"
            ).textContent = btnText;
        }
        if (oosProducts.length > 0) {
            addToCartButton().disabled = true;
        } else {
            if (
                !this.querySelector(".build-set__error-banner").classList.contains(
                    "hidden"
                )
            ) {
                this.querySelector(".build-set__error-banner").classList.add("hidden");
            }
        }
    }

    removeSelectedProducts() {
        const { elements: { selectedProductsDisplay } } = this;
        selectedProductsDisplay().addEventListener("click", (event) => {
            const removeButton = event.target.closest(this.selectors.productCard).querySelector(
                this.selectors.removeCard
            );
            if (removeButton) {
                const unSelectedCard = removeButton.closest(
                    ".build-set__selected-product-card"
                );
                if (unSelectedCard) {
                    const variantId = unSelectedCard.dataset.variantId;
                    const productCardInList = this.querySelector(
                        `product-card[data-variant-id="${variantId}"]`
                    );
                    this.removeProduct(productCardInList, variantId);
                    this.updateUIState();
                }
            }
        });
    }
    updateProductCardsDisabledState() {
        const hasReachedMax = this.selectedProducts.length >= this.minSelectionsForCart;

        this.querySelectorAll('[data-build-set-product="product-card"]').forEach((card) => {
            const button = card.querySelector('[data-build-set-product="product-selection"]');
            const isCardDisabled = card.classList.contains("disabled");
            const isSelected = card.dataset.buildSetSelected === "true";
            button.disabled = (hasReachedMax && !isSelected) || isCardDisabled;
        });
    }

    updateItemCountText() {
        const {elements: { itemCountElement }} = this;
        const itemsToAdd = this.minSelectionsForCart - this.selectedProducts.length;
        let text;
        if (itemsToAdd > 0) text = `Add ${this.minSelectionsForCart} items`;
        else text = `Total`;
        itemCountElement().textContent = text;
    }
    
    updateGiftProduct() {
        const nonGiftProducts = this.selectedProducts.filter(
            (product) => !product.gift
        );
        const hasGiftItem = this.selectedProducts.some(
            (item) => item.gift === "true"
        );
        const giftProduct = this.querySelector(this.selectors.giftProduct);
        const giftText = this.querySelector(this.selectors.giftText);
        if (nonGiftProducts.length >= this.minSelectionsForCart) {
            if (!hasGiftItem) {
                let gift_variant_id = giftProduct.dataset.giftId;
                const gift_product_data = {
                    variantId: gift_variant_id,
                    gift: "true",
                    price: parseInt(giftProduct.dataset.productPrice),
                    path: window.location.pathname
                };
                this.selectedProducts.push(gift_product_data);
                giftProduct.classList.remove("is-disabled");
                giftText.textContent = 'Gift';
            }
            if (giftProduct.classList.contains("is-disabled")) {
                giftProduct.classList.remove("is-disabled");
                giftText.textContent = 'Gift';
            }
        } else {
            if (hasGiftItem) {
                this.selectedProducts = this.selectedProducts.filter(
                    (item) => !item.gift
                );
                giftProduct.classList.add("is-disabled");
                giftText.textContent = 'Unlock Gift';
            }
        }
    }

    async handleAddToCart(event) {
        const {elements: { addToCartButton }} = this;
        event.preventDefault();

        // Validate minimum selection
        if (this.selectedProducts.length < this.minSelectionsForCart) {
            document.dispatchEvent(
                new CustomEvent("toast:show", {
                    detail: {
                        message: `Please select at least ${this.minSelectionsForCart} items.`,
                        tone: "error",
                        duration: 4000,
                    },
                })
            );
            return;
        }

        addToCartButton().disabled = true;
        addToCartButton().setAttribute("aria-busy", "true");
        document.documentElement.dispatchEvent(
            new CustomEvent("theme:loading:start", { bubbles: true })
        );

        try {
            const promises = [];
            const fetchData = async(url, payload) => {
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  });
                
                  if (!response.ok) {
                    const errorData = await response.json();
                    throw { status: response.status, message: errorData.message || "Unknown error" };
                  }
                
                  return response.json();
            }

            // --- Identify items to remove ---
            let itemsToRemove = this.existingCartProducts.filter((item) => {
                const noLongerSelected = !this.selectedProducts.some(
                    (selected) => String(selected.variantId) === String(item.variantId)
                );
                const unwantedGift = !this.giftProductStatus && item.gift;
                return noLongerSelected || unwantedGift; 
            });

            itemsToRemove = itemsToRemove.filter((item) => {
                if(!item !== 'gift' && !item.path ) {
                    return item
                }
            })

            if (itemsToRemove.length) {
                const updates = Object.fromEntries(
                    itemsToRemove
                        .filter((item) => item.key)
                        .map((item) => [item.key, 0])
                );

                if (Object.keys(updates).length) {
                    promises.push(await fetchData(`${window.Shopify.routes.root}cart/update.js`, { updates }));
                }
            }

            // --- Identify items to add ---
            const newItemsToAdd = this.selectedProducts.filter(
                (selected) =>
                    !this.existingCartProducts.some(
                        (existing) =>
                            String(existing.variantId) === String(selected.variantId)
                    )
            );

            if (newItemsToAdd.length) {
                const items = newItemsToAdd.map((product) => ({ 
                    id: product.variantId,
                    quantity: 1,
                    properties: {
                    ...(product.gift !== "true" && {
                        _is_byos: "true",
                        _byos_url: window.location.pathname,
                        _byos_title: this.setProductTitle,
                        _byos_image: this.setProductImage
                    }),
                    ...(product.gift === "true" && { _is_byos_gift: "true",  _byos_url: window.location.pathname }),
                    },
                }));

                promises.push(await fetchData(`${window.Shopify.routes.root}cart/add.js`, { items }));
            }

            // Execute all cart updates
            await Promise.all(promises);

            // Refresh and open cart drawer
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { 
                bubbles: true,
                detail: {
                    openGwpDrawer: true
                }
            }));

            document.querySelector('[aria-controls="cart-drawer"]').click();

        } catch (error) {
            console.error("Error updating cart:", error);
            const message = error.message || "Something went wrong while updating the cart.";
            document.dispatchEvent(
                new CustomEvent("toast:show", {
                    detail: {
                        message: message,
                        tone: "error",
                        duration: 4000,
                    },
                })
            );
            window.location.reload();
        } finally {
            addToCartButton().disabled = false;
            addToCartButton().removeAttribute("aria-busy");
            document.documentElement.dispatchEvent(
                new CustomEvent("theme:loading:end", { bubbles: true })
            );
            this.checkInitialCartState("reload");
        }
    }

    updateUIState() {
        if(this.giftProductStatus) this.updateGiftProduct();
        this.updateSelectedProductsDisplay();
        this.updatePriceDisplay();
        this.updateProgressBar();
        this.updateAddToCartButton();
        this.updateProductCardsDisabledState();
        this.updateItemCountText();
    }

    connectedCallback() {
        this.updateAddToCartButton();
        this.removeSelectedProducts();
        this.checkInitialCartState();

    }
    handleSidebarToggle() {
        const toggleIcon = this.querySelector(this.selectors.sidebarToggle);
        const productContainers = this.querySelectorAll(".build-set__selected-products");

        if (!toggleIcon || !productContainers.length) return;

        toggleIcon.classList.toggle("is-active");

        productContainers.forEach((container) => {
            const isHidden = container.style.display === "none" || !container.style.display;
            container.style.display = isHidden ? "flex" : "none";
        });
    }

}

customElements.define("byos-builder", BYOSBuilder);
